import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import getConfig from 'next/config'
import { Images } from '../../assets'
import { DashboardJettyAPI } from '../../api'
import { connectJettyMapsSocket } from '../../api/dashboard/dashboard-jetty/jetty-maps.socket'
import { ConvertLocalTimeGet, Lookup } from '../../utils'
import { SET_USER_PROFILE_LOCATION } from '../../redux/actions'
import { handleListLocations } from './helper.js'
import {
  isDummyJettyEnabled,
  buildDummyJettyData,
} from './dashboard-jetty-morowali/dashboard-jetty-map.helper'

import Modal from './dashboard-jetty-modal.container'
import VesselSorong from './dashboard-jetty-sorong/dashboard-jetty.container'
import VesselMorowali from './dashboard-jetty-morowali/dashboard-jetty.container'
import VesselAdangBay from './dashboard-jetty-adangbay/dashboard-jetty.container'
import VesselKariangau from './dashboard-jetty-kariangau/dashboard-jetty.container'
import { DropdownLocSide } from './dashboard-dropdown.container'
import { MOROWALI_MAP_WIDTH } from './dashboard-jetty-morowali/dashboard-jetty.styled'

import {
  Jetty,
  MapCompass,
  Title,
  Width,
  Detail,
  Legend,
  Wrapper,
  TabTitle,
  Circular,
  JettyBox,
  Overflow,
  HeaderTitle,
  JettyWrapper,
  LegendDetail,
  LegendStatus,
  LocationSide,
  HeaderWrapper,
  DetailWrapper,
  ChartContainer,
} from './dashboard-jetty.styled'

const DashboardJetty = () => {
  const dispatch = useDispatch()
  const { publicRuntimeConfig } = getConfig()
  const [modal, setModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [current, setCurrent] = useState(true)
  const [firstLoad, setFirstLoad] = useState(true)
  const [dataJetty, setDataJetty] = useState([])
  const [metadata, setMetadata] = useState({
    target: 0,
    actual: 0
  })
  const [jettyLocations, setJettyLocations] = useState([])
  const [dataNextComing, setDataNextComing] = useState([])
  const appSize = useSelector((state) => state.appReducer)
  const [intervalDataJetty, setIntervalDataJetty] = useState(15)
  // Live updates arrive over the WebSocket (see jetty-maps.socket.js) instead
  // of the old 15-minute REST poll. usePolling only turns true as a fallback
  // if the socket can't connect after a few retries, so the page still gets
  // data if the WS endpoint is unreachable.
  const [usePolling, setUsePolling] = useState(false)
  const socketRef = useRef(null)
  const width = '100vw'
  const height = 'calc(88.72vh)'
  const { Location } = useSelector((state) => state.usersProfileReducer)
  const { UserLocations } = useSelector((state) => state.usersLoginReducer)
  const ListLocations = process.env.JETTY_MAPS_LOCATIONS || publicRuntimeConfig.JETTY_MAPS_LOCATIONS

  const _getFilterSearch = () => {
    const filter =
      Location && Location.LocationID
        ? `[{ "type": "number", "value": "${Location.LocationID}", "field": "location_code" }]`
        : '[]'
    const search = Location?.LocationDescription?.replace(/\s/g, '')?.toLowerCase() === 'adangbay' ? 'AdangBay' : ''
    return { filter, search }
  }

  const _getData = async () => {
    setLoading(true)
    const { filter, search } = _getFilterSearch()
    const { data, metadata } = await (current
      ? DashboardJettyAPI.GetOnProgress(filter, search)
      : DashboardJettyAPI.GetNextComing(filter))

    if (data) {
      if (current) {
        setDataJetty(data)
        setMetadata(metadata)
      } else {
        setDataNextComing(data)
      }
    }
    setLoading(false)
  }

  const _getIntervalDataJetty = async () => {
    try {
      const filter =
        '[{"field":"type","type":"text","value":"Interval_Jetty"},{"field":"is_active","type":"bool","value":"true"}]'
      const { data } = await DashboardJettyAPI.GetIntervalDataJetty(0, 100, filter)
      if(data && data[0] && data[0].Value) {
        setIntervalDataJetty(parseInt(data[0].Value))
      }
    } catch (err) {
      console.log('error', err)
    }
  }

  const _getMapsJettyLocation = async () => {
    try {
      const filter =
        '[{"field":"type","type":"text","value":"JettyMapsLocation"},{"field":"is_active","type":"bool","value":"true"}]'
      const { data } = await DashboardJettyAPI.GetMapsJettyLocation(0, 10, filter)
      if(data && data.length > 0) {
        setJettyLocations(data)
      }
    } catch (err) {
      console.log('error', err)
    }
  }

  const isAdangbay = useMemo(() => {
    const name = Location.LocationDescription?.replace(/\s/g, '')?.toLowerCase() || ''
    return name === 'adangbay'
  }, [jettyLocations, Location.LocationID])

  const isMorowali = useMemo(() => {
    const name = Location.LocationDescription?.replace(/\s/g, '')?.toLowerCase() || ''
    return name === 'morowali'
  }, [jettyLocations, Location.LocationID])

  // Dev-only preview (?dummyJetty=1): replaces the Morowali jetty data (map and
  // side list) with a full set of vessels.
  const [isDummy, setIsDummy] = useState(false)
  useEffect(() => {
    setIsDummy(isDummyJettyEnabled())
  }, [])
  const dummyJetty = useMemo(
    () => (isDummy && isMorowali ? buildDummyJettyData() : null),
    [isDummy, isMorowali]
  )

  useEffect(() => {
    _getData()
    _getIntervalDataJetty()
    setFirstLoad(false)
  }, [])

  useEffect(() => {
    _getMapsJettyLocation()
  }, [Location.LocationDescription])

  // Open the live jetty_maps WebSocket once and keep it open for the life of
  // this page; location/filter changes just re-send a "subscribe" message on
  // the same connection (see the effect below) instead of reconnecting.
  useEffect(() => {
    socketRef.current = connectJettyMapsSocket({
      onUpdate: ({ type, data }) => {
        if (type === 'jetty_maps') {
          setDataJetty(data.data)
          setMetadata(data.metadata)
        } else if (type === 'jetty_maps_nextcoming') {
          setDataNextComing(data.data)
        }
      },
      onFallback: () => setUsePolling(true),
    })
    return () => socketRef.current?.close()
  }, [])

  useEffect(() => {
    const { filter, search } = _getFilterSearch()
    socketRef.current?.subscribe({
      location: Location?.LocationDescription,
      filter,
      search,
    })
  }, [Location.LocationDescription, Location.LocationID])

  // Fallback only: if the socket above can't connect after a few retries,
  // fall back to the old REST polling so the page still gets data.
  useEffect(() => {
    if (!usePolling) return
    const intervalId = setInterval(()=>{
      _getData()
    }, intervalDataJetty * 60 * 1000)
    return () => clearInterval(intervalId)
  }, [intervalDataJetty, usePolling])

  useEffect(() => {
    if (!firstLoad) {
      _getData()
    }
    setModal(false)
  }, [current, Location])

  const _setLocation = (value) => {
    const valueLocation = UserLocations.find((e) => e.LocationDescription === value)

    dispatch({
      type: SET_USER_PROFILE_LOCATION,
      payload: valueLocation, //? valueLocation : { LocationID: 0 },
    })
  }

  const refetchJettyData = async () => {
    const filter =
      Location && Location.LocationID
        ? `[{ "type": "number", "value": "${Location.LocationID}", "field": "location_code" }]`
        : '[]'
    const { data, metadata } = await DashboardJettyAPI.GetOnProgress(filter, 'AdangBay')

    if (data) {
      setDataJetty(data)
      setMetadata(metadata)
    }
  }

  const _mappingMapsBG = () => {
    let name = Location?.LocationDescription?.replace(/\s/g, '')?.toLowerCase()

    switch (name) {
    case 'kariangau':
      return {
        jetty: Images.MapsJetty,
        background: Images.MapsJettyBackground
      }
    case 'sorong':
      return {
        jetty: Images.MapsJettySorong,
        background: Images.MapsJettyBackgroundSorong
      }
    case 'morowali':
      return {
        jetty: Images.BerthMorowaliMap,
        background: ''
      }
    case 'adangbay':
      return {
        jetty: '',
        background: Images.MapsJettyBackgroundAdangBay
      }
    default:
      return {
        jetty: Images.MapsJetty,
        background: Images.MapsJettyBackground
      }
    }
  }

  const _mappingMapsVessel = () => {
    let allJetty = dataJetty.filter((dt) => {
      let data = dt
      if (dt.Detail && dt.Detail.length > 0) {
        data.Detail = dt.Detail.filter((dtl) => dtl.Value.toLowerCase() == 'in progress'
          || (dtl.Value.toLowerCase() == 'completed'
            && moment().format('DD-MM-YYYY') == ConvertLocalTimeGet(dt.Location, dtl.LastStatusDate, 'DD-MM-YYYY')))
      }
      return data
    })

    let name = Location?.LocationDescription?.replace(/\s/g, '')?.toLowerCase()
    if (name === 'morowali') {
      return <VesselMorowali dataJetty={dummyJetty || dataJetty} isDummy={!!dummyJetty} />
    }
    if (name === 'sorong') {
      return <VesselSorong dataJetty={allJetty} />
    }
    if (name === 'kariangau' || name === 'balikpapan') {
      return <VesselKariangau dataJetty={dataJetty} />
    }
    if (name === 'adangbay') {
      return <VesselAdangBay dataJetty={dataJetty} metadata={metadata} refetchJettyMaps={refetchJettyData} />
    }
    return null
  }

  const _legend = () => {
    const legendList = isAdangbay
      ? Lookup.LEGEND_JETTY_MAPS.filter(lg => lg.label !== 'Delay')
      : Lookup.LEGEND_JETTY_MAPS
    const locationSideWidth = isAdangbay ? '32rem' : '19.27vw'

    return (
      <>
        <LocationSide width={locationSideWidth}>
          <DropdownLocSide
            location={Location}
            dataList={handleListLocations(UserLocations, ListLocations)}
            setLocation={_setLocation}
            width={locationSideWidth}
          />
        </LocationSide>
        <Legend width={locationSideWidth} top='16.9vh'>
          {legendList.map((lg, i) => (
            <LegendDetail key={i}>
              <LegendStatus color={lg.color} />
              {lg.label}
            </LegendDetail>
          ))}
        </Legend>
        {_chart()}
      </>
    )
  }

  const _chart = () => {
    return (
      <ChartContainer
        innerHeight={appSize.height - 200} // after location finish//
        top='22.02vh'
        width={isAdangbay ? '32rem' : '19.27vw'}
      >
        <TabTitle>
          <Title
            id='maps-jetty-chart-tab-on-progress'
            active={current}
            onClick={() => setCurrent(true)}
          >
            On Progress
          </Title>
          <Title
            id='maps-jetty-chart-tab-up-coming'
            active={!current}
            onClick={() => setCurrent(false)}
          >
            Up Coming
          </Title>
        </TabTitle>
        {_headerList()}
        {_contentList()}
      </ChartContainer>
    )
  }

  const _headerList = () => {
    const header = isAdangbay
      ? Lookup.HEADER_JETTY_MAPS.filter((dt) => dt.label !== 'Progress')
      : isMorowali
      ? Lookup.HEADER_JETTY_MAPS.filter(
          (dt) => dt.label !== 'Progress' && dt.label !== 'Commence Loading'
        )
      : Lookup.HEADER_JETTY_MAPS.filter((dt) => dt.label !== 'Commence Loading')
    return (
      <HeaderWrapper isAdangbay={isAdangbay}>
        {header.filter((dt) => current || dt.current === current).map((hd, i) => {
          return (
            <HeaderTitle key={i} width={current ? hd.width : hd.next_width}>
              {hd.label !== 'ATA' || current ? hd.label : 'Planning Arrival'}
            </HeaderTitle>
          )
        })}
      </HeaderWrapper>
    )
  }

  const _contentList = () => {
    return (
      <Overflow>
        {(current ? (dummyJetty || dataJetty) : dataNextComing).map((dt, i) => {
          return (
            <div key={i}>
              <JettyWrapper>
                <JettyBox width={isAdangbay ? '100%' : '17.18vw'}>{dt.Jetty_Name}</JettyBox>
              </JettyWrapper>
              {dt.Detail &&
                dt.Detail.map((dtl, i) => {
                  const location = dtl.Location?.toLowerCase()?.replace(/\s/, '')
                  const isShowProgress = location !== 'morowali' && location !== 'adangbay'
                  return (
                    <DetailWrapper
                      current={current}
                      id={`detail-wrapper-${dtl.SrNo}`}
                      key={i}
                      onClick={() => {
                        setDetail(dtl)
                        setModal(true)
                      }}
                    >
                      {current && (
                        <Width
                          width={'10%'}
                          data-indicator-delay-value={
                            dtl.Indicator_Delay === '#8EBB3C'
                              ? 'green'
                              : dtl.Indicator_Delay === '#F2C300'
                                ? 'yellow'
                                : 'red'
                          }
                          id={`indicator-delay-${dtl.Jetty_Name.replace(/\s/, '')}-${i}`}
                        >
                          <LegendStatus color={dtl.Indicator_Delay} />
                        </Width>
                      )}
                      <Width width={current ? '100%' : '35%'}>
                        <Detail>{dtl.Vessel_Name}</Detail>
                      </Width>
                      <Width width={current ? '100%' : '27.5%'}>
                        <Detail>
                          {ConvertLocalTimeGet(dt.Location, current ? dtl.Ata : dtl.Plan_Arrival, 'DD-MM-YYYY')}
                          <br />
                          {ConvertLocalTimeGet(dt.Location, current ? dtl.Ata : dtl.Plan_Arrival, 'HH:mm')}
                        </Detail>
                      </Width>
                      {current && isAdangbay && (
                        <Width width={'100%'}>
                          <Detail>
                            {ConvertLocalTimeGet(dt.Location, dtl.CommenceLoadingDate, 'DD-MM-YYYY HH:mm')}
                          </Detail>
                        </Width>
                      )}
                      <Width width={current ? '100%' : '27.5%'}>
                        <Detail>
                          {ConvertLocalTimeGet(dt.Location, dtl.Plan_Depart, 'DD-MM-YYYY')}
                          <br />
                          {ConvertLocalTimeGet(dt.Location, dtl.Plan_Depart, 'HH:mm')}
                        </Detail>
                      </Width>
                      {/* //hide  Total_Progress_Activity when location is Morowali */}
                      {current && isShowProgress && (
                        <Width
                          width={'100%'}
                          data-progress-value={dtl.Total_Progress_Activity}
                          id={`total-progress-activity-${dtl.Jetty_Name.replace(/\s/, '')}-${i}`}
                        >
                          <Circular
                            text={`${dtl.Total_Progress_Activity}%`}
                            value={dtl.Total_Progress_Activity}
                          />
                        </Width>
                      )}
                    </DetailWrapper>
                  )
                })}
            </div>
          )
        })}
      </Overflow>
    )
  }

  return (
    <Wrapper
      isAdangbay={isAdangbay}
      width={width}
      height={height}
      backgroundImage={_mappingMapsBG() && _mappingMapsBG().background}
    >
      {!isAdangbay && (
        <Jetty
          src={_mappingMapsBG() && _mappingMapsBG().jetty}
          targetHeight={height}
          targetWidth={isMorowali ? MOROWALI_MAP_WIDTH : undefined}
        />
      )}
      {isMorowali && <MapCompass src={Images.CompassMorowali} mapWidth={MOROWALI_MAP_WIDTH} alt='' />}
      {!loading && _mappingMapsVessel()}
      {_legend()}
      {modal && <Modal detail={detail} close={() => setModal(false)} current={current} />}
    </Wrapper>
  )
}

export default DashboardJetty